import { useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { HardDrive, Trash2 } from 'lucide-react';
import { Button, ConfirmDialog } from '@/components/ui';
import { idempotencyKey } from '@/utils/idempotency';
import { useNodeRemnants, useReclaimRemnant } from '@/hooks/useNodes';
import { toast } from '@/store/toastStore';
import { errorText } from '@/utils/errors';
import { formatRelative } from '@/utils/format';
import type { NodeRemnantView } from '@/types/api';

/**
 * Stopped copies of sites that have moved off this server (ADR-0021): what is
 * using its disk without serving anything.
 */
export function NodeRemnants({ nodeId, mayRemove }: { readonly nodeId: string; readonly mayRemove: boolean }): ReactNode {
  const [removing, setRemoving] = useState<NodeRemnantView | null>(null);
  const key = useRef('');
  // Removals queued from here: shown as under way, and the list is re-read
  // until they have gone from it.
  const [pending, setPending] = useState<ReadonlySet<string>>(new Set());

  const remnants = useNodeRemnants(nodeId, pending);

  const reclaim = useReclaimRemnant(
    { nodeId },
    {
      onSuccess: (_result, { remnantId }) => {
        setPending((current) => new Set([...current, remnantId]));
          toast.success('Удаление копии поставлено в очередь');
        setRemoving(null);
      },
    },
  );

  const items = remnants.data ?? [];
  if (items.length === 0) return null;

  return (
    <div className="stack stack-tight">
      <h4 className="row-tight">
        <HardDrive size={16} aria-hidden="true" /> Остановленные копии · {items.length}
      </h4>
      <p className="field-hint">Копии перенесённых сайтов. Остановлены, занимают место на диске.</p>
      {items.map((remnant) => (
        <div key={remnant.id} className="row-between">
          <span>
            {remnant.siteDeleted ? (
              <span>
                {remnant.siteName} <span className="muted">(сайт удалён)</span>
              </span>
            ) : (
              <Link to={`/sites/${remnant.siteId}`}>{remnant.siteName}</Link>
            )}
            <span className="muted"> · {formatRelative(remnant.createdAt)}</span>
          </span>
          {pending.has(remnant.id) ? (
            <span className="muted text-sm">удаляется…</span>
          ) : mayRemove ? (
            <Button
              size="sm"
              variant="danger"
              icon={Trash2}
              onClick={() => {
                key.current = idempotencyKey('site.reclaim');
                reclaim.reset();
                setRemoving(remnant);
              }}
            >
              Удалить
            </Button>
          ) : null}
        </div>
      ))}

      <ConfirmDialog
        open={removing !== null}
        title="Удалить остановленную копию?"
        description={removing ? `Контейнер, файлы и база копии «${removing.siteName}» будут удалены с этого сервера.` : undefined}
        confirmLabel="Удалить копию"
        destructive
        busy={reclaim.isPending}
        error={reclaim.isError ? errorText(reclaim.error) : undefined}
        onConfirm={() => {
          if (removing) reclaim.mutate({ remnantId: removing.id, key: key.current });
        }}
        onClose={() => setRemoving(null)}
      />
    </div>
  );
}
